import React from "react";

/**
 * When a day's art was initialized from a clique site (an x-n450s ecosystem
 * site at https://<slug>.x.n450s.com), the backend tags the AOTD with an
 * `origin`. On visitor-facing surfaces the CLIQUE, not the username, is the
 * author: the clique name replaces the author name and links out to the site.
 */
export interface CliqueOrigin {
  slug: string;
  name: string;
  source: "clique";
}

/** A clique slug is a subdomain label: one segment, lowercase, up to 64 chars. */
const CLIQUE_SLUG_RE = /^[a-z0-9][a-z0-9-]{0,63}$/;

/** The clique site URL, or null when the slug can't safely build a subdomain. */
export function cliqueUrl(origin?: CliqueOrigin | null): string | null {
  if (!origin || !CLIQUE_SLUG_RE.test(origin.slug)) return null;
  return `https://${origin.slug}.x.n450s.com`;
}

interface CliqueAttributionProps {
  /** The clique origin, when the day was initialized from one. */
  origin?: CliqueOrigin | null;
  /** The author node to show when there is no clique origin (the username). */
  fallback: React.ReactNode;
}

/**
 * Renders the author for an AOTD surface: the linked clique name when an
 * `origin` is present (unlinked if its slug is unsafe), otherwise the plain
 * `fallback` (the username). The link inherits the surrounding text color so it
 * blends into whatever caption/quote/stat line it sits in.
 */
const CliqueAttribution: React.FC<CliqueAttributionProps> = ({ origin, fallback }) => {
  if (!origin) return <>{fallback}</>;

  const url = cliqueUrl(origin);
  if (!url) return <>{origin.name}</>;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title={`${origin.name} on x.n450s.com`}
      style={{
        color: "inherit",
        textDecoration: "underline",
        textUnderlineOffset: "2px",
        textDecorationThickness: "1px",
      }}
    >
      {origin.name}
    </a>
  );
};

export default CliqueAttribution;
